import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heart, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { BirthDatePicker, BirthTimePicker } from "@/components/BirthDateTime";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";

const input = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-xl px-3 py-2.5 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60";

export default function Together() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ name: "", birth_date: "", birth_time: "", birth_place: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/public/together/${token}`).then(({ data }) => {
      setInfo(data);
      setF((s) => ({ ...s, name: data.partner_name }));
      if (data.ref) localStorage.setItem("astra_ref", data.ref);
    }).catch((e) => setError(e.response?.data?.detail || "This invitation link isn't valid any more"));
  }, [token]);

  const join = async () => {
    setBusy(true);
    try {
      if (!user) await register({ ...f, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      const { data } = await api.post(`/synastry/together/${token}/accept`);
      navigate(`/synastry/${data.report_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong. Please try again."); setBusy(false); }
  };
  const ready = user || (f.name && f.birth_date && f.email && f.password.length >= 6);

  return (
    <PublicShell testId="together-page">
      <div className={`px-5 pt-6 fade-up ${WIDE ? "lg:max-w-xl lg:mx-auto lg:pt-14" : ""}`}>
        {error ? <p data-testid="together-error" className="text-center text-[#A7B0C0] py-20">{error}</p> : !info ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin gold-text" size={28} /></div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Heart size={11} className="inline -mt-0.5 mr-1" />An invitation</p>
            <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-3" data-testid="together-title">{info.from_name} made a synastry reading for you both</h1>
            <p className="text-base text-[#D6D0C5] mb-2">You're <span className="gold-text">{info.archetype}</span>: “{info.tagline}”.</p>
            <p className="text-base text-[#A7B0C0] mb-7">Add your own birth details so the reading is exact, and you'll both see it in your accounts.</p>
            <div className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-3xl p-6 space-y-2.5" data-testid="together-form">
              {!user && (
                <>
                  <input data-testid="together-name" className={input} placeholder="Your first name" value={f.name} onChange={(e) => set("name")(e.target.value)} />
                  <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Date of birth</label><BirthDatePicker testId="together-dob" value={f.birth_date} onChange={set("birth_date")} /></div>
                  <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Time of birth (optional, makes it more accurate)</label><BirthTimePicker testId="together-tob" value={f.birth_time} onChange={set("birth_time")} /></div>
                  <input data-testid="together-pob" className={input} placeholder="City of birth" value={f.birth_place} onChange={(e) => set("birth_place")(e.target.value)} />
                  <input data-testid="together-email" type="email" className={input} placeholder="Email" value={f.email} onChange={(e) => set("email")(e.target.value)} />
                  <input data-testid="together-password" type="password" className={input} placeholder="Choose a password (6+ characters)" value={f.password} onChange={(e) => set("password")(e.target.value)} />
                </>
              )}
              {user && <p className="text-base text-[#D6D0C5]">Signed in as {user.name}. Your saved birth details will be used.</p>}
              <p className="text-xs text-[#A7B0C0] flex gap-2 pt-1"><ShieldCheck size={14} className="gold-text shrink-0" />Your birth details are used only to calculate your chart accurately. {info.from_name} sees the reading, never your email or password.</p>
              <button data-testid="btn-together-join" onClick={join} disabled={busy || !ready || info.joined}
                className="w-full mt-3 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={18} /> : info.joined ? "Already joined" : user ? "See our reading" : "Create my free account and see our reading"}
              </button>
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
